import { KDF_ITERATIONEN, leiteSchluesselAb, neuerSalt } from "../shared/kdf.js";
import type { Benutzer, KdfParameter } from "../shared/types.js";

/**
 * Der CSRF-Token wird beim Laden des Profils geholt und bei jedem
 * mutierenden Request mitgeschickt. Er ist an die Session gebunden (HMAC
 * über die Session-ID) und übersteht damit auch einen Angreifer, der auf
 * einer Nachbar-Subdomain Cookies setzen könnte.
 */
let csrf = "";
export const setzeCsrf = (t: string) => {
  csrf = t;
};

/** Streckungsparameter der laufenden Sitzung, für den Passwortwechsel. */
let kdf: KdfParameter | null = null;
export const holeKdf = () => kdf;

export class ApiFehler extends Error {
  constructor(
    public readonly status: number,
    nachricht: string,
  ) {
    super(nachricht);
  }
}

async function anfrage<T>(pfad: string, init?: RequestInit): Promise<T> {
  const kopf: Record<string, string> = { "Content-Type": "application/json" };
  if (init?.method && init.method !== "GET") kopf["X-CSRF-Token"] = csrf;

  const antwort = await fetch(`/api${pfad}`, {
    ...init,
    headers: { ...kopf, ...(init?.headers as Record<string, string>) },
    credentials: "same-origin",
  });

  const text = await antwort.text();
  const daten = text ? JSON.parse(text) : null;

  if (!antwort.ok) throw new ApiFehler(antwort.status, daten?.fehler ?? "Unerwarteter Fehler.");
  return daten as T;
}

export const api = {
  get: <T>(pfad: string) => anfrage<T>(pfad),
  post: <T>(pfad: string, body: unknown) =>
    anfrage<T>(pfad, { method: "POST", body: JSON.stringify(body) }),
  patch: <T>(pfad: string, body: unknown) =>
    anfrage<T>(pfad, { method: "PATCH", body: JSON.stringify(body) }),
  // PUT für Ressourcen, die als Ganzes ersetzt werden — die Getreidemischung
  // ist eine Liste, kein Feld.
  put: <T>(pfad: string, body: unknown) =>
    anfrage<T>(pfad, { method: "PUT", body: JSON.stringify(body) }),
};

/**
 * Anmelden in zwei Schritten.
 *
 * Erst den Salt holen, dann im Browser strecken, dann nur den abgeleiteten
 * Schlüssel senden. Das Klartextpasswort verlässt diese Funktion nie.
 * Für unbekannte E-Mails liefert der Server einen Pseudo-Salt, damit der
 * erste Schritt nicht verrät, welche Konten es gibt.
 */
export async function anmelden(email: string, passwort: string): Promise<void> {
  const p = await api.post<KdfParameter>("/login/salt", { email });
  const abgeleitet = await leiteSchluesselAb(passwort, p.salt, p.iterationen);
  await api.post("/login", { email, abgeleitet });
}

export async function abmelden(): Promise<void> {
  await api.post("/logout", {});
}

export async function ladeProfil(): Promise<Benutzer> {
  const daten = await api.get<{ benutzer: Benutzer; csrf: string; kdf: KdfParameter }>("/me");
  setzeCsrf(daten.csrf);
  kdf = daten.kdf;
  return daten.benutzer;
}

export async function aenderePasswort(alt: string, neu: string): Promise<void> {
  if (!kdf) throw new Error("Sitzung unvollständig geladen.");
  // Der neue Salt wird bei jedem Wechsel erneuert.
  const salt = neuerSalt();
  await api.post("/passwort", {
    alt: await leiteSchluesselAb(alt, kdf.salt, kdf.iterationen),
    neu: await leiteSchluesselAb(neu, salt, KDF_ITERATIONEN),
    neuerSalt: salt,
    iterationen: KDF_ITERATIONEN,
  });
  kdf = { salt, iterationen: KDF_ITERATIONEN };
}

export async function istEingerichtet(): Promise<boolean> {
  const d = await api.get<{ eingerichtet: boolean }>("/setup/status");
  return d.eingerichtet;
}

export async function legeErstenAdminAn(
  email: string,
  name: string,
  passwort: string,
): Promise<void> {
  const salt = neuerSalt();
  await api.post("/setup/erster-admin", {
    email,
    name,
    abgeleitet: await leiteSchluesselAb(passwort, salt, KDF_ITERATIONEN),
    salt,
    iterationen: KDF_ITERATIONEN,
  });
}

/**
 * Nach einem Deploy zeigen offene Tabs noch die alten Asset-Namen; ein
 * nachgeladener Chunk existiert dann nicht mehr. Statt der rohen
 * Browsermeldung "Failed to fetch dynamically imported module" bekommt der
 * Nutzer den einzig sinnvollen Hinweis.
 */
export function nachladeFehler(e: unknown): string {
  const m = e instanceof Error ? e.message : String(e);
  return /dynamically imported module|Importing a module script failed|Failed to fetch/i.test(m)
    ? "Die Anwendung wurde soeben aktualisiert. Bitte die Seite neu laden (Strg+R bzw. Cmd+R)."
    : m;
}
